import { SortModelItem } from 'ag-grid-community';
import { Injectable } from '@angular/core';
import { PlannableRangeDto } from '@core/services/dtos/plannable-range.dto';
import { HttpQueryBuilder } from '@portal-shared/app/builders/http-query.builder';
import { SortDirectionEnum } from '@portal-shared/app/enums/sort-directions.enum';
import { SliderRangeValueModel } from '@portal-shared/app/models/slider-range-value-model';
import { PlannableGroupingHelper } from '@resource-management/task-board/models/plannable-grouping.enum';
import { AllyaStorageService } from '@services/allya-storage.service';
import { PlannablesFilter } from '@services/models/plannables-filterby.model';
import { BehaviorSubject } from 'rxjs';
import { FilterUpdate } from './models/filter-update.model';

@Injectable({
  providedIn: 'root'
})
export class PlannablesFiltersService {
  private hourRangeLimits = { minValue: -1000, maxValue: 1000 };

  private filterName = 'PlannablesFilters';

  private defaultFilters$ = new BehaviorSubject<PlannablesFilter>({
    isHideEmptyProjects: false,
    hoursToPlanRange: {},
    sortDirection: SortDirectionEnum.Asc,
    orderBy: 'cardTitle',
    groupSelection: ['project']
  });

  private filters$ = new BehaviorSubject<FilterUpdate>({
    key: undefined,
    filters: this.getDefaultFilters()
  });

  public constructor(private allyaStorageService: AllyaStorageService) {}

  public init() {
    const filters = this.allyaStorageService.getItem<PlannablesFilter>(this.filterName);
    if (filters) {
      this.filters$.next({ filters });
    } else {
      this.clearFilters();
    }
  }

  public updateHourRangeLimits(rangeLimits: PlannableRangeDto) {
    this.hourRangeLimits = rangeLimits;

    // Update the default filters with range limits
    const defaultFilters = this.getDefaultFilters();
    defaultFilters.hoursToPlanRange.lowerValue = rangeLimits.minValue;
    defaultFilters.hoursToPlanRange.upperValue = rangeLimits.maxValue;
    this.defaultFilters$.next(defaultFilters);

    const filters = this.filters$.value.filters;

    // Set the range filter to the default range if the current range is outside the new range limits
    const updatedRange: SliderRangeValueModel = { ...filters.hoursToPlanRange, ...rangeLimits };

    let shouldUpdateFilters = false;

    if (updatedRange.lowerValue && updatedRange.lowerValue < rangeLimits.minValue) {
      updatedRange.lowerValue = undefined;
      shouldUpdateFilters = true;
    } else if (updatedRange.upperValue && updatedRange.upperValue > rangeLimits.maxValue) {
      updatedRange.upperValue = undefined;
      shouldUpdateFilters = true;
    }

    if (!shouldUpdateFilters) return; // No need to update the range (avoid unnecessary updates)

    filters.hoursToPlanRange = updatedRange;

    this.filters$.next({ filters });
  }

  public getFilters() {
    return this.filters$.getValue().filters;
  }

  public getDefaultFilters() {
    return this.defaultFilters$.getValue();
  }

  public getHourRangeLimits() {
    return this.hourRangeLimits;
  }

  public getHiddenActiveFiltersCount() {
    const filters = this.getFilters();
    const defaultFilters = this.getDefaultFilters();

    let count = 0;
    if (filters.isHideEmptyProjects !== defaultFilters.isHideEmptyProjects) {
      count++;
    }
    if (
      ![undefined, defaultFilters.hoursToPlanRange.lowerValue].includes(filters.hoursToPlanRange.lowerValue) ||
      ![undefined, defaultFilters.hoursToPlanRange.upperValue].includes(filters.hoursToPlanRange.upperValue)
    ) {
      count++;
    }
    if (filters.orderBy !== defaultFilters.orderBy || filters.sortDirection !== defaultFilters.sortDirection) {
      count++;
    }

    return `${count}`;
  }

  public clearFilters(options?: { keepSearch?: boolean; keepGroups?: boolean }) {
    const filters = this.getFilters();
    const newFilters = { ...this.getDefaultFilters() };

    if (options?.keepGroups) {
      newFilters.groupSelection = filters.groupSelection;
    }
    if (options?.keepSearch) {
      newFilters.searchKeys = filters.searchKeys;
    }

    this.filters$.next({
      filters: newFilters
    });
  }

  public updateFilter<TKey extends keyof PlannablesFilter>(key: TKey, value: PlannablesFilter[TKey]) {
    const filters = this.getFilters();

    filters[key] = value;
    this.filters$.next({
      key,
      filters
    });
    this.allyaStorageService.setItem(this.filterName, filters);
    return filters;
  }

  public observeChanges() {
    return this.filters$.asObservable();
  }

  public observeDefaultChanges() {
    return this.defaultFilters$.asObservable();
  }

  public apply(query: HttpQueryBuilder) {
    const filters = this.getFilters();
    const defaultFilters = this.getDefaultFilters();

    if (filters.isHideEmptyProjects !== undefined) {
      query.queryParam('hideEmptyProjects', filters.isHideEmptyProjects);
    }

    if (![undefined, defaultFilters.hoursToPlanRange.lowerValue].includes(filters.hoursToPlanRange.lowerValue)) {
      query.queryParam('greaterThanOrEqual', filters.hoursToPlanRange.lowerValue);
    }
    if (![undefined, defaultFilters.hoursToPlanRange.upperValue].includes(filters.hoursToPlanRange.upperValue)) {
      query.queryParam('lessThanOrEqual', filters.hoursToPlanRange.upperValue);
    }

    if (filters.searchKeys) {
      query.search(filters.searchKeys.join(','));
    }

    if (filters.orderBy === 'cardTitle') {
      const orderBy = PlannableGroupingHelper.addMissingGroups([...filters.groupSelection]);

      const sortModel: SortModelItem[] = orderBy.map(group => {
        return { colId: PlannableGroupingHelper.toCode(group), sort: filters.sortDirection };
      });

      query.sort(sortModel);
    } else if (filters.orderBy === 'plannableHours') {
      query.sort([{ colId: 'plannableHours', sort: filters.sortDirection }]);
    }

    return query;
  }
}
